import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Facebook, Twitter, Instagram, Linkedin, Youtube, Github, Mail, Star, Loader2 } from 'lucide-react';
import { useGetPublicHellocians } from '../services/hellocian.service';
import { useGetMe } from '../services/auth/auth';
import { createDirectChat } from '../services/chat.service';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomAlert from '../components/CustomAlert';

const HireTalents = () => {
  const navigate = useNavigate();
  const { data: publicData, isLoading: isPublicLoading } = useGetPublicHellocians();
  const { data: currentUser } = useGetMe();
  
  const [alert, setAlert] = React.useState({ show: false, message: '', type: 'success' });
  const [isCreatingChat, setIsCreatingChat] = React.useState(null);

  const hellocians = publicData?.hellocians || [];

  const handleMessage = async (hellocian) => {
    if (!currentUser) {
      setAlert({
        show: true,
        message: 'Please log in to message our talents.',
        type: 'error'
      });
      return;
    }

    try {
      setIsCreatingChat(hellocian._id);
      const data = await createDirectChat(hellocian._id);
      const chat = data.chat;
      
      const basePath = currentUser.role === 'hellocian' ? '/hellocians/messages' : '/clients/messages';
      window.location.href = `${basePath}/${chat._id || chat.id}`;
    } catch (error) {
      console.error('Chat creation error:', error);
      setAlert({
        show: true,
        message: error.response?.data?.error || 'Failed to start chat. Please try again.',
        type: 'error'
      });
    } finally {
      setIsCreatingChat(null);
    }
  };

  const SocialLink = ({ type, url }) => {
    if (!url) return null;
    
    const icons = {
      facebook: Facebook,
      twitter: Twitter,
      instagram: Instagram,
      linkedin: Linkedin,
      youtube: Youtube,
      github: Github
    };
    
    const Icon = icons[type];
    if (!Icon) return null;

    return (
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="p-2 bg-gray-50 rounded-full text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-all duration-300"
        title={type.charAt(0).toUpperCase() + type.slice(1)}
      >
        <Icon size={16} />
      </a>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      {alert.show && (
        <CustomAlert 
          type={alert.type} 
          message={alert.message} 
          onClose={() => setAlert({ ...alert, show: false })} 
        />
      )}

      <main className="flex-grow pt-16 pb-14 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4 tracking-tight">
              Our Elite <span className="text-[#0077B6]">Hellocian</span> Talents
            </h1>
            <p className="text-[15px] text-gray-600 max-w-2xl mx-auto">
              Work with vetted professionals dedicated to bringing your projects to life with precision and excellence.
            </p>
          </div>

          {isPublicLoading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="w-7 h-7 text-blue-500 animate-spin mb-4" />
              <p className="text-gray-500 font-normal text-[15px]">Discovering top talents for you...</p>
            </div>
          ) : hellocians.length === 0 ? (
            <div className="bg-white rounded-xl p-6 text-center border border-gray-100">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Star className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className="text-xl font-medium text-gray-800 mb-2">No Hellocians available yet</h3>
              <p className="text-gray-500 text-[15px]">Our elite team is growing. Check back soon for new talents!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {hellocians.map((h) => (
                <div 
                  key={h._id} 
                  className="group bg-white rounded-xl p-3 hover:shadow-sm transition-all duration-500 border border-gray-100 overflow-hidden relative"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-16 -mt-16 group-hover:bg-blue-100 transition-colors duration-500" />
                  
                  <div className="relative flex flex-col items-center text-center">
                    {/* Profile Picture */}
                    <div className="w-16 h-16 rounded-full border-2 border-white overflow-hidden mb-4 ring-2 ring-blue-50 ring-offset-2 group-hover:scale-105 transition-transform duration-500">
                      <img 
                        src={h.profilePicture || "/api/placeholder/100/100"} 
                        alt={`${h.firstName} ${h.lastName}`}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Meta Info */}
                    <h2 className="text-[16px] font-medium text-gray-900 transition-colors">
                      {h.firstName} {h.lastName}
                    </h2>
                    <p className="text-blue-500 text-[11px] font-normal mb-1">@{h.username}</p>

                    {/* Bio */}
                    <p className="text-gray-600 text-[12px] leading-relaxed mb-3 line-clamp-3 italic">
                      {h.bio || "Crafting excellence and delivering results with a pasión for innovation."}
                    </p>

                    {/* Socials & Actions */}
                    <div className="w-full pt-3 border-t border-gray-50 flex items-center justify-between">
                      <div className="flex gap-1">
                        {h.socials && Object.entries(h.socials).map(([type, url]) => (
                          <SocialLink key={type} type={type} url={url} />
                        ))}
                      </div>

                      {h.directMessages && (
                        <button
                          onClick={() => handleMessage(h)}
                          disabled={isCreatingChat === h._id}
                          className="flex items-center gap-2 px-5 py-2.5 text-[#0077B6] rounded-xl text-sm font-medium hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50"
                        >
                          {isCreatingChat === h._id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <>
                              <MessageCircle size={16} />
            
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HireTalents;
